import { FC } from "react"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import Dropdown from "@/components/shared/Dropdown"
import TextInput from "@/components/shared/TextInput"
import { TestId } from "@/constants/globals"
import { userFieldConfig } from "@/pages/Settings/config"
import { USER_ROLES } from "@/pages/Settings/Users/constants"
import { useUserForm } from "@/pages/Settings/Users/hooks/useUserForm"
import { UserFormData } from "@/pages/Settings/Users/types"
import { getErrorMessage } from "@/utils/index"
import "./index.scss"

interface UsersFormProps {
   submitError: number | null
   onSave: (data: UserFormData) => void
}

const UsersForm: FC<UsersFormProps> = ({ submitError, onSave }) => {
   const {
      state,
      validations,
      errors,
      touched,
      handleChange,
      handleRoleChange,
      handleBlur,
      isFormValid,
      handleSubmit,
   } = useUserForm(onSave)
   const { firstName, lastName, email, phoneNumber } = state
   const {
      firstName: { placeholder: firstNamePlaceholder, testId: firstNameTestId },
      lastName: { placeholder: lastNamePlaceholder, testId: lastNameTestId },
      email: {
         placeholder: emailPlaceholder,
         testId: emailTestId,
         helperTextTestId: emailHelperTextTestId,
      },
      phoneNumber: {
         placeholder: phoneNumberPlaceholder,
         testId: phoneNumberTestId,
      },
      role: { placeholder: rolePlaceholder },
   } = userFieldConfig

   const getFieldProps = (field: keyof typeof state) => ({
      error: touched[field] && !validations[field],
      helperText: touched[field] ? errors[field] : undefined,
   })

   return (
      <div className="user-form">
         <h1 className="user-form__title">New user</h1>
         <div className="user-form__inputs">
            <div className="user-form__input-wrapper">
               <TextInput
                  name="firstName"
                  value={firstName}
                  placeholder={firstNamePlaceholder}
                  onChange={handleChange}
                  onBlur={() => handleBlur("firstName")}
                  withHelper
                  testId={firstNameTestId}
                  {...getFieldProps("firstName")}
               />
            </div>
            <div className="user-form__input-wrapper">
               <TextInput
                  name="lastName"
                  placeholder={lastNamePlaceholder}
                  value={lastName}
                  onChange={handleChange}
                  onBlur={() => handleBlur("lastName")}
                  withHelper
                  testId={lastNameTestId}
                  {...getFieldProps("lastName")}
               />
            </div>
         </div>
         <div className="user-form__input-group">
            <TextInput
               name="email"
               value={email}
               placeholder={emailPlaceholder}
               onChange={handleChange}
               onBlur={() => handleBlur("email")}
               withHelper
               testId={emailTestId}
               helperTextTestId={emailHelperTextTestId}
               {...getFieldProps("email")}
            />
         </div>
         <div className="user-form__input-group">
            <TextInput
               name="phoneNumber"
               value={phoneNumber}
               placeholder={phoneNumberPlaceholder}
               onChange={handleChange}
               onBlur={() => handleBlur("phoneNumber")}
               withHelper
               testId={phoneNumberTestId}
               {...getFieldProps("phoneNumber")}
            />
         </div>
         <Dropdown
            placeholder={rolePlaceholder}
            options={USER_ROLES}
            onSelectionChange={handleRoleChange}
            data-testid={TestId.USER_FORM_ROLES_DROPDOWN}
         />
         <div className="user-form__submit-button-wrapper">
            <Button
               iconName="envelope"
               iconPosition={ButtonIconPosition.LEFT}
               disabled={!isFormValid}
               onClick={handleSubmit}
               data-testid={TestId.USER_FORM_SAVE_BUTTON}
            >
               Save
            </Button>
            <div
               className="user-form__error"
               data-testid={TestId.USER_FORM_ERROR_MESSAGE}
            >
               {submitError && getErrorMessage(submitError)}
            </div>
         </div>
      </div>
   )
}

export default UsersForm
